<template>
    <!-- Popover d'options d'une carte catalogue. Deux modes :
         - multi  : variantes d'un billet simple → une quantité PAR variante, on peut en prendre
                    plusieurs d'un coup (2 adultes + 3 enfants) ;
         - simple : durées / prices_by_times → un seul choix, une quantité. -->
    <div class="cv3-popover" :style="popoverStyle" :class="{ 'popover--multi': multi }" @click.stop>
        <!-- Fixed Header -->
        <div class="cv3-popover-header">
            <span class="cv3-popover-title" :title="title">{{ displayTitle }}</span>
        </div>

        <!-- Scrollable Options List -->
        <div class="cv3-popover-body">
            <template v-if="multi">
                <!-- Multi Mode (variantes de billet simple) -->
                <div v-for="(opt, oi) in options" :key="oi" class="cv3-opt-row" :class="{ active: quantities[oi] > 0 }">
                    <div class="cv3-opt-info">
                        <div class="cv3-opt-name">{{ optionName(opt) }}</div>
                        <div class="cv3-opt-price">{{ optionPrice(opt) }}</div>
                    </div>
                    <!-- Dedicated Stepper per line -->
                    <div class="cv3-counter">
                        <button
                            type="button"
                            class="cv3-counter-btn minus"
                            :disabled="(quantities[oi] || 0) <= 0"
                            @click="setQty(oi, (quantities[oi] || 0) - 1)"
                        >
                            <MinusIcon :size="14" />
                        </button>
                        <input
                            type="number"
                            class="cv3-counter-val"
                            min="0"
                            :value="quantities[oi] || 0"
                            @click="selectAllInput"
                            @change="onCounterInput(oi, $event)"
                            @keyup.enter="onCounterInput(oi, $event)"
                            @keyup.escape="resetCounterInput($event, quantities[oi] || 0)"
                        />
                        <button type="button" class="cv3-counter-btn plus" @click="setQty(oi, (quantities[oi] || 0) + 1)">
                            <PlusIcon :size="14" />
                        </button>
                    </div>
                </div>
            </template>

            <template v-else>
                <!-- Single Mode (durées / prices_by_times) -->
                <button
                    v-for="(opt, oi) in options"
                    :key="oi"
                    type="button"
                    class="cv3-opt-row cv3-opt-row--single"
                    :class="{ active: selectedOpt === opt }"
                    @click="selectedOpt = opt"
                >
                    <!-- Pastille de sélection (Radio dot) -->
                    <span class="cv3-radio-dot" :class="{ checked: selectedOpt === opt }"></span>
                    <div class="cv3-opt-info">
                        <div class="cv3-opt-name">{{ optionName(opt) }}</div>
                        <div class="cv3-opt-price">{{ optionPrice(opt) }}</div>
                    </div>
                </button>
            </template>
        </div>

        <!-- Fixed Footer -->
        <div class="cv3-popover-footer">
            <!-- If single mode: show recalled option label + quantity stepper (disabled when none chosen) -->
            <div v-if="!multi" class="cv3-footer-stepper" :class="{ 'cv3-footer-stepper--disabled': !selectedOpt }">
                <div class="cv3-footer-stepper-meta">
                    <span class="cv3-footer-stepper-qty-label">{{ $t('checkout.payment.quantity') || 'Quantité' }}</span>
                    <span class="cv3-footer-stepper-opt-name">
                        {{ selectedOpt ? optionName(selectedOpt) : '-' }}
                    </span>
                </div>
                <div class="cv3-counter">
                    <button
                        type="button"
                        class="cv3-counter-btn minus"
                        :disabled="!selectedOpt || qty <= 1"
                        @click="qty = Math.max(1, qty - 1)"
                    >
                        <MinusIcon :size="14" />
                    </button>
                    <input
                        type="number"
                        class="cv3-counter-val"
                        min="1"
                        :value="selectedOpt ? qty : 0"
                        :disabled="!selectedOpt"
                        @click="selectAllInput"
                        @change="onSingleInput($event)"
                        @keyup.enter="onSingleInput($event)"
                        @keyup.escape="resetSingleInput($event)"
                    />
                    <button type="button" class="cv3-counter-btn plus" :disabled="!selectedOpt" @click="qty++">
                        <PlusIcon :size="14" />
                    </button>
                </div>
            </div>

            <!-- Validation Button -->
            <button
                type="button"
                class="cv3-validate"
                :disabled="!canValidate"
                :class="{ 'cv3-validate--disabled': !canValidate }"
                @click="validate"
            >
                <template v-if="multi">
                    <!-- Total price and quantity recall for multi mode validation -->
                    {{
                        totalQty > 0
                            ? $t('checkout.v3.tickets_selection_total', { count: totalQty, total: formatPrice(totalPrice) })
                            : $t('checkout.v3.validate')
                    }}
                </template>
                <template v-else>
                    <!-- Total price and quantity recall for single mode validation -->
                    {{
                        selectedOpt
                            ? `${$t('checkout.v3.validate')} · ${formatPrice(selectedOpt.price * qty)}€`
                            : $t('checkout.v3.validate')
                    }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import { Minus as MinusIcon, Plus as PlusIcon } from 'lucide-vue'

export default {
    name: 'OptionsPopover',
    components: { MinusIcon, PlusIcon },
    props: {
        // Options figées par le parent (références stables : `selectedOpt === opt` en dépend).
        options: { type: Array, required: true },
        multi: { type: Boolean, default: false },
        title: { type: String, default: '' },
    },
    data() {
        return {
            selectedOpt: null,
            qty: 1,
            quantities: [],
            windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
            popoverStyle: {},
        }
    },
    created() {
        this.resetQuantities()
        this.autoSelectSingleOption()
    },
    mounted() {
        window.addEventListener('resize', this.onResize)
        this.$nextTick(() => {
            this.adjustPosition()
        })
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
    },
    watch: {
        options() {
            this.selectedOpt = null
            this.qty = 1
            this.resetQuantities()
            this.autoSelectSingleOption()
            this.$nextTick(() => {
                this.adjustPosition()
            })
        },
    },
    computed: {
        totalQty() {
            return this.quantities.reduce((sum, q) => sum + (Number(q) || 0), 0)
        },
        totalPrice() {
            return this.options.reduce((sum, opt, i) => sum + (Number(opt.price) || 0) * (Number(this.quantities[i]) || 0), 0)
        },
        canValidate() {
            return this.multi ? this.totalQty > 0 : !!this.selectedOpt
        },
        displayTitle() {
            if (!this.title) return ''
            let maxChars = 20
            if (this.windowWidth > 600) {
                maxChars = 35
            } else if (this.windowWidth > 400) {
                maxChars = 25
            }
            if (this.title.length > maxChars) {
                return this.title.slice(0, maxChars) + '...'
            }
            return this.title
        },
    },
    methods: {
        onResize() {
            this.windowWidth = window.innerWidth
            this.adjustPosition()
        },
        selectAllInput(e) {
            e.target.select()
        },
        adjustPosition() {
            if (typeof window === 'undefined' || !this.$el || !this.$el.parentElement) return

            const parentRect = this.$el.parentElement.getBoundingClientRect()
            const margin = 12
            const popoverWidth = 290

            // Find catalog container boundaries, fallback to viewport
            const container = this.$el.closest('.cv3-catalog')
            let minLeft = margin
            let maxLeft = window.innerWidth - popoverWidth - margin

            if (container) {
                const containerRect = container.getBoundingClientRect()
                minLeft = containerRect.left + margin
                maxLeft = containerRect.right - popoverWidth - margin
            }

            // Centered target position in viewport coordinates
            let targetLeft = parentRect.left + parentRect.width / 2 - popoverWidth / 2

            // Clamp position within boundaries
            if (targetLeft < minLeft) {
                targetLeft = minLeft
            } else if (targetLeft > maxLeft) {
                targetLeft = maxLeft
            }

            // Convert to parent-relative coordinate
            const relativeLeft = targetLeft - parentRect.left

            this.popoverStyle = {
                left: `${relativeLeft}px`,
                transform: 'none',
            }
        },
        resetQuantities() {
            this.quantities = this.options.map(() => 0)
        },
        autoSelectSingleOption() {
            if (!this.multi && Array.isArray(this.options) && this.options.length === 1) {
                this.selectedOpt = this.options[0]
            }
        },
        setQty(index, value) {
            this.$set(this.quantities, index, Math.max(0, value))
        },
        onCounterInput(index, e) {
            const v = parseInt(e.target.value, 10)
            this.setQty(index, isNaN(v) ? 0 : Math.max(0, v))
            e.target.value = this.quantities[index] || 0
        },
        resetCounterInput(e, fallback) {
            e.target.value = fallback
        },
        onSingleInput(e) {
            const v = parseInt(e.target.value, 10)
            this.qty = isNaN(v) || v < 1 ? 1 : v
            e.target.value = this.qty
        },
        resetSingleInput(e) {
            e.target.value = this.qty
        },
        formatPrice(v) {
            return (Math.round((Number(v) || 0) * 100) / 100).toString().replace('.', ',')
        },
        optionName(opt) {
            if (opt._ticket) return opt._ticket.name
            const d = Number(opt.duration) || 0
            return d > 0
                ? d >= 60
                    ? `${Math.floor(d / 60)}${this.$t('checkout.v3.unit_h')}${
                          d % 60 ? ' ' + (d % 60) + this.$t('checkout.v3.unit_m') : ''
                      }`
                    : `${d} ${this.$t('checkout.v3.unit_m')}`
                : this.$t('checkout.v3.all_day')
        },
        optionPrice(opt) {
            return `${this.formatPrice(opt.price)}€`
        },
        // Émet TOUJOURS une liste [{ opt, qty }] — le parent traite les deux modes pareil.
        validate() {
            if (!this.canValidate) return
            const picks = this.multi
                ? this.options.map((opt, i) => ({ opt, qty: Number(this.quantities[i]) || 0 })).filter((p) => p.qty > 0)
                : [{ opt: this.selectedOpt, qty: this.qty }]
            this.$emit('validate', picks)
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-popover {
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translateX(-50%);
    width: 290px;
    max-width: calc(100vw - 24px);
    z-index: 30;
    background: #161029;
    border: 1px solid rgba(171, 110, 220, 0.35);
    border-radius: 14px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
    overflow: hidden;
    font-family:
        'Poppins',
        system-ui,
        -apple-system,
        sans-serif;
    color: #fff;
    cursor: default;
}

.cv3-popover-header {
    display: flex;
    align-items: center;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: #1d1638;
}

.cv3-popover-title {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.cv3-popover-body {
    flex: 1;
    min-height: 0;
    max-height: 220px;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    /* Scrollbar styling */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.12);
        border-radius: 999px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
}

.cv3-opt-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 10px;
    border-radius: 10px;
    background: #241c44;
    border: 1px solid transparent;
    color: #fff;
    text-align: left;
    transition:
        background 0.15s,
        border-color 0.15s;

    &.active {
        border-color: rgba(171, 110, 220, 0.75);
        background: #2e2352;
    }
}

.cv3-opt-row--single {
    width: 100%;
    cursor: pointer;
    justify-content: flex-start;
}

.cv3-radio-dot {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    border-radius: 999px;
    border: 2px solid rgba(255, 255, 255, 0.28);
    background: transparent;
    position: relative;
    transition:
        border-color 0.15s,
        background 0.15s;

    &.checked {
        border-color: #ab6edc;
        background: #7d2dbd;
        box-shadow: inset 0 0 0 2px #161029;
    }
}

.cv3-opt-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cv3-opt-name {
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cv3-opt-price {
    font-size: 12px;
    font-weight: 600;
    color: #c398e6;
}

/* Stepper & counters */
.cv3-counter {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.cv3-counter-btn {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-family: inherit;
    transition:
        background 0.15s,
        opacity 0.15s;

    &.minus {
        border: 1px solid rgba(255, 255, 255, 0.18);
        background: transparent;
        color: #c6d0dc;

        &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.08);
        }
        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }

    &.plus {
        border: none;
        background: #7d2dbd;
        color: #fff;

        &:hover {
            background: #8f3ad2;
        }
    }
}

.cv3-counter-val {
    min-width: 16px;
    width: 32px;
    height: 26px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    background: rgba(125, 45, 189, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    outline: none;
    padding: 0;
    -moz-appearance: textfield;
    appearance: none;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    &:focus {
        border-color: #c398e6;
        box-shadow: 0 0 0 3px rgba(171, 110, 220, 0.35);
        outline: none;
    }
    &:disabled {
        opacity: 0.4;
    }
}

/* Footer layout */
.cv3-popover-footer {
    padding: 10px 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: #1d1638;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cv3-footer-stepper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    background: #241c44;
    border: 1px solid rgba(171, 110, 220, 0.35);
    transition:
        opacity 0.15s,
        border-color 0.15s;
}

.cv3-footer-stepper--disabled {
    opacity: 0.5;
    border-color: rgba(255, 255, 255, 0.08);
}

.cv3-footer-stepper-meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.cv3-footer-stepper-qty-label {
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #889bb4;
}

.cv3-footer-stepper-opt-name {
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.cv3-validate {
    width: 100%;
    padding: 10px;
    border-radius: 10px;
    border: none;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s,
        opacity 0.15s;

    background: rgba(255, 255, 255, 0.06);
    color: #6b7480;

    &:not(:disabled) {
        background: #7d2dbd;
        color: #fff;

        &:hover {
            background: #8f3ad2;
        }
    }

    &:disabled,
    &.cv3-validate--disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
}
</style>
